import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import { FiMapPin, FiSearch, FiX } from 'react-icons/fi';
import './LocationPicker.css';

const libraries = ['places'];

const mapContainerStyle = {
    width: '100%',
    height: '300px',
    borderRadius: '8px'
};

const defaultCenter = {
    lat: 28.6692,  // Default to Ghaziabad
    lng: 77.4538
};

const LocationPicker = ({ onLocationSelect, initialLocation, onClose }) => {
    const [markerPosition, setMarkerPosition] = useState(initialLocation || defaultCenter);
    const [address, setAddress] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const autocompleteRef = useRef(null);
    const mapRef = useRef(null);

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_FIREBASE_API_KEY;

    // Handle map click to place marker
    const handleMapClick = useCallback((e) => {
        const newPosition = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
        };
        setMarkerPosition(newPosition);
        reverseGeocode(newPosition);
    }, []);

    // Handle marker drag end
    const handleMarkerDragEnd = useCallback((e) => {
        const newPosition = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
        };
        setMarkerPosition(newPosition);
        reverseGeocode(newPosition);
    }, []);

    // Reverse geocode to get address from lat/lng
    const reverseGeocode = async (position) => {
        setIsLoading(true);
        try {
            const geocoder = new window.google.maps.Geocoder();
            const response = await geocoder.geocode({ location: position });
            if (response.results && response.results[0]) {
                setAddress(response.results[0].formatted_address);
            }
        } catch (error) {
            console.error('Geocoding failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle place selection from autocomplete
    const handlePlaceSelect = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place.geometry && place.geometry.location) {
                const newPosition = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                };
                setMarkerPosition(newPosition);
                setAddress(place.formatted_address || '');

                // Pan map to new location
                if (mapRef.current) {
                    mapRef.current.panTo(newPosition);
                }
            }
        }
    };

    // Handle autocomplete load
    const handleAutocompleteLoad = (autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    // Handle map load
    const handleMapLoad = (map) => {
        mapRef.current = map;
    };

    // Confirm location selection
    const handleConfirm = () => {
        if (onLocationSelect) {
            const locationData = {
                lat: markerPosition.lat,
                lng: markerPosition.lng,
                address: address || `${markerPosition.lat.toFixed(4)}, ${markerPosition.lng.toFixed(4)}`
            };
            onLocationSelect(locationData);
        }
    };

    // Get current location using browser geolocation
    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            setIsLoading(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const newPosition = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    setMarkerPosition(newPosition);
                    reverseGeocode(newPosition);
                    if (mapRef.current) {
                        mapRef.current.panTo(newPosition);
                    }
                },
                (error) => {
                    console.error('Error getting location:', error);
                    setIsLoading(false);
                }
            );
        }
    };

    if (!apiKey) {
        return (
            <div className="location-picker-error">
                <p>Google Maps API key is not configured.</p>
            </div>
        );
    }

    return (
        <div className="location-picker-container">
            <div className="location-picker-header">
                <h3><FiMapPin /> Select Exact Delivery Location</h3>
                {onClose && (
                    <button className="close-picker-btn" onClick={onClose}>
                        <FiX />
                    </button>
                )}
            </div>

            <LoadScript googleMapsApiKey={apiKey} libraries={libraries}>
                <div className="location-search-box">
                    <Autocomplete
                        onLoad={handleAutocompleteLoad}
                        onPlaceChanged={handlePlaceSelect}
                        options={{
                            componentRestrictions: { country: 'in' } // Restrict to India
                        }}
                    >
                        <div className="search-input-wrapper">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search for your address..."
                                className="location-search-input"
                            />
                        </div>
                    </Autocomplete>
                    <button className="current-location-btn" onClick={getCurrentLocation} title="Use my current location">
                        📍 Use Current Location
                    </button>
                </div>

                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={markerPosition}
                    zoom={15}
                    onClick={handleMapClick}
                    onLoad={handleMapLoad}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false
                    }}
                >
                    <Marker
                        position={markerPosition}
                        draggable={true}
                        onDragEnd={handleMarkerDragEnd}
                        animation={window.google?.maps?.Animation?.DROP}
                    />
                </GoogleMap>
            </LoadScript>

            <div className="selected-location-info">
                {isLoading ? (
                    <p className="loading-address">Getting address...</p>
                ) : address ? (
                    <div className="address-display">
                        <FiMapPin />
                        <p>{address}</p>
                    </div>
                ) : (
                    <p className="hint-text">Click on the map or search to select your exact delivery location</p>
                )}
            </div>

            <div className="location-coordinates">
                <span>Lat: {markerPosition.lat.toFixed(6)}</span>
                <span>Lng: {markerPosition.lng.toFixed(6)}</span>
            </div>

            <button
                className="confirm-location-btn"
                onClick={handleConfirm}
                disabled={!address}
            >
                Confirm This Location
            </button>
        </div>
    );
};

export default LocationPicker;
